'use client';

import { getOrCreateId } from '@repo/utils/static-ids';
import { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useReferrerContext } from '../../context/referrer';
import { useAnalytics } from '../../hooks/useAnalytics';
import type { PagePayload } from '../../Shibuya';
import { AnalyticsPageViewContext } from './Context';

export interface PageViewProps {
  name?: string;
  contentId?: PagePayload['content_id'];
  contentSource?: PagePayload['content_source'];
  dangerouslyIgnoreCanonicalUrl?: boolean;
  additionalProperties?: Partial<PagePayload>;
}

export const AnalyticsPageView = ({
  name = '',
  contentId,
  contentSource,
  dangerouslyIgnoreCanonicalUrl,
  children,
  additionalProperties,
}: PropsWithChildren<PageViewProps>) => {
  const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
  const [pageViewReady, setPageViewReady] = useState(false);
  const pageViewIdRef = useRef('');
  const pageViewId = getOrCreateId(pageViewIdRef);
  const { referrer, isInitialPageLoad, setInitialPageLoaded } = useReferrerContext();

  useEffect(() => {
    if (!analytics) return;
    if (deviceId) {
      const payload: PagePayload = {
        deployable_name: deployableName,
        deployable_version: deployableVersion,
        pageview_id: pageViewId,
        traditional_device_id: deviceId,
        content_id: contentId,
        content_source: contentSource,
        referrer: isInitialPageLoad ? document.referrer : (referrer ?? document.referrer),
      };

      if (dangerouslyIgnoreCanonicalUrl) {
        payload.path = window.location.pathname;
        payload.url = window.location.href;
      }

      const mergedPayload = { ...payload, ...additionalProperties };

      const context = {
        app: {
          name: deployableName,
          version: gitSha,
        },
      };

      if (name) {
        analytics.page(name, mergedPayload, {
          context,
        });
      } else {
        analytics.page(mergedPayload, undefined, {
          context,
        });
      }
      setPageViewReady(true);

      if (isInitialPageLoad) {
        setInitialPageLoaded();
      }
    }
  }, [deviceId]);

  return (
    <AnalyticsPageViewContext.Provider
      value={{ pageName: name, pageViewId, pageViewReady, contentId, contentSource }}
    >
      {children}
    </AnalyticsPageViewContext.Provider>
  );
};
